/**
 * WatchlistDetailPage — `/kyc/watchlist` (queue) + `/kyc/watchlist/:id` (detail).
 *
 * Lists active watchlist hits via `GET /v1/kyc/watchlist/queue`. For a single
 * hit detail (`:id`), uses `GET /v1/kyc/watchlist/hits/:id` so already-reviewed
 * hits remain accessible (the queue list is filtered to ACTIVE only).
 */
import { type JSX, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { getWatchlistHit, listWatchlistHits, reviewWatchlistHit } from '../../lib/api/kyc';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

function ErrorBanner({ title, message }: { readonly title: string; readonly message: string }): JSX.Element {
  return (
    <div
      className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900 space-y-1"
      data-testid="watchlist-error"
    >
      <p className="font-semibold">{title}</p>
      <p className="text-red-800/90">{message}</p>
    </div>
  );
}

export function WatchlistDetailPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const queue = useQuery({
    queryKey: ['kyc', 'watchlist', 'queue'],
    queryFn: () => listWatchlistHits({ pageSize: 100 }),
    retry: false,
    enabled: !id,
  });

  const denied =
    queue.isError && queue.error instanceof ApiError && (queue.error.status === 401 || queue.error.status === 403);

  if (id) {
    return <WatchlistHitDetail hitId={id} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Watchlist queue" description="Active screening hits awaiting review." />

      {queue.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <ErrorBanner title="Permission required" message="Token missing Kyc/read. Contact the platform admin." />
      ) : queue.isError ? (
        <ErrorBanner title="Service unreachable" message={(queue.error as Error).message} />
      ) : (queue.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Watchlist queue is clear.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="watchlist-queue-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Profile</th>
                <th className="text-left px-4 py-2">List</th>
                <th className="text-left px-4 py-2">Match</th>
                <th className="text-left px-4 py-2">Confidence</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Detected</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {queue.data?.items.map((h) => (
                <tr key={h.id} className="hover:bg-gray-50" data-testid={`watchlist-row-${h.id}`}>
                  <td className="px-4 py-2 font-mono text-[10px] text-muted">{h.profileId}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className="inline-flex items-center gap-1 text-danger">
                      <ShieldAlert className="w-3.5 h-3.5" /> {h.listSource}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-700">{h.matchKind}</td>
                  <td className="px-4 py-2 text-xs font-bold">{Math.round(h.confidence)}%</td>
                  <td className="px-4 py-2"><StatusBadge status={h.status} /></td>
                  <td className="px-4 py-2 text-xs text-muted">{new Date(h.detectedAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/kyc/watchlist/${encodeURIComponent(h.id)}`}
                      className="text-xs font-semibold text-nipost-green hover:underline"
                      data-testid={`watchlist-review-link-${h.id}`}
                    >
                      Review →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface DetailProps {
  readonly hitId: string;
}

function WatchlistHitDetail({ hitId }: DetailProps): JSX.Element {
  const hitQuery = useQuery({
    queryKey: ['kyc', 'watchlist', 'hit', hitId],
    queryFn: () => getWatchlistHit(hitId),
    retry: false,
  });
  const [decision, setDecision] = useState<'FALSE_POSITIVE' | 'CONFIRMED' | 'CLEARED' | ''>('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);

  const hit = hitQuery.data ?? null;
  const notFound = hitQuery.isError && hitQuery.error instanceof ApiError && hitQuery.error.status === 404;
  const denied = hitQuery.isError && hitQuery.error instanceof ApiError && (hitQuery.error.status === 401 || hitQuery.error.status === 403);

  async function submit(): Promise<void> {
    if (!decision) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitOk(false);
    try {
      await reviewWatchlistHit(hitId, decision, note.trim().length > 0 ? note.trim() : undefined);
      setSubmitOk(true);
      void hitQuery.refetch();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setSubmitError('Permission required: Kyc/review');
      } else {
        setSubmitError((e as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Watchlist hit review"
        description={`Hit ${hitId}`}
        actions={
          <Link to="/kyc/watchlist" className="text-xs font-semibold text-nipost-green hover:underline">
            ← Back to queue
          </Link>
        }
      />

      {hitQuery.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : notFound ? (
        <ErrorBanner title="Hit not found" message={`Hit ${hitId} does not exist.`} />
      ) : denied ? (
        <ErrorBanner title="Permission required" message="Token missing Kyc/read. Contact the platform admin." />
      ) : hitQuery.isError ? (
        <ErrorBanner title="Service unreachable" message={(hitQuery.error as Error).message} />
      ) : !hit ? (
        <ErrorBanner title="Hit not available" message="The hit could not be loaded." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="watchlist-hit-detail">
          <div className="bg-white border border-border rounded-xl p-5 space-y-3">
            <h2 className="text-sm font-semibold text-gray-900">Match data</h2>
            <div>
              <p className="text-xs text-muted">Profile</p>
              <p className="text-xs font-mono" data-testid="hit-profile-id">{hit.profileId}</p>
            </div>
            <div>
              <p className="text-xs text-muted">List</p>
              <p className="text-sm font-medium" data-testid="hit-list-source">{hit.listSource}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Confidence</p>
              <p className="text-sm font-bold">{Math.round(hit.confidence)}%</p>
            </div>
            <div>
              <p className="text-xs text-muted">Match kind</p>
              <p className="text-sm">{hit.matchKind}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Status</p>
              <p className="text-sm" data-testid="hit-status"><StatusBadge status={hit.status} /></p>
            </div>
            <div>
              <p className="text-xs text-muted">Detected</p>
              <p className="text-sm">{new Date(hit.detectedAt).toLocaleString()}</p>
            </div>
            {hit.reviewedAt ? (
              <div>
                <p className="text-xs text-muted">Reviewed</p>
                <p className="text-sm">{new Date(hit.reviewedAt).toLocaleString()}</p>
                {hit.reviewNote ? (
                  <p className="text-xs text-muted mt-1">Note: {hit.reviewNote}</p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="bg-white border border-border rounded-xl p-5 space-y-3">
            <h2 className="text-sm font-semibold text-gray-900">Decision</h2>
            {hit.status !== 'ACTIVE' ? (
              <p className="text-xs text-muted">
                This hit was already reviewed ({hit.status}). Re-review requires a fresh screening run.
              </p>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  {(['CONFIRMED', 'FALSE_POSITIVE', 'CLEARED'] as const).map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${
                        decision === opt ? 'border-nipost-green bg-nipost-green-light' : 'border-border'
                      }`}
                    >
                      <input
                        type="radio"
                        name="decision"
                        value={opt}
                        data-testid={`decision-${opt}`}
                        checked={decision === opt}
                        onChange={() => setDecision(opt)}
                      />
                      <span className="text-sm font-medium">{opt.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Notes</label>
                  <textarea
                    data-testid="decision-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    placeholder="Provide rationale for your decision…"
                  />
                </div>
                <button
                  type="button"
                  data-testid="submit-decision"
                  onClick={submit}
                  disabled={!decision || submitting}
                  className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-nipost-green-dark"
                >
                  {submitting ? 'Submitting…' : 'Submit decision'}
                </button>
                {submitError ? <p className="text-xs text-danger">{submitError}</p> : null}
                {submitOk ? <p className="text-xs text-success" data-testid="decision-recorded">Decision recorded.</p> : null}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
